import throttle from 'lodash-es/throttle';
import Cookies from 'js-cookie';
import { DomService } from '../dom';

export interface GoogleUser {
  email: string;
  // eslint-disable-next-line camelcase
  given_name: string;
  // eslint-disable-next-line camelcase
  family_name: string;
  /** Token expiration */
  exp: number;
  /** Organization */
  hd: string;
  [key: string]: string | number;
}

export const GoogleService = {
  LOGIN_COMPLETE_EVENT: 'rocheLoginComplete',
  googleCookie: 'google_token',
  scriptSrc: 'https://accounts.google.com/gsi/client',
  init: undefined as Promise<void> | undefined,

  initialize() {
    if (GoogleService.init) {
      // Do not init twice, either init is already completed or still in progress
      return GoogleService.init;
    }

    const { gapiClientId } = DomService.getGlobalConfig();
    if (!gapiClientId) {
      return Promise.reject(new Error('gapiClientId is undefined'));
    }

    const init = new Promise<void>((resolve) => {
      const { scriptSrc } = GoogleService;
      const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${scriptSrc}"]`);

      const onLoad = () => {
        window.google.accounts.id.initialize({
          client_id: gapiClientId,
          callback: GoogleService.handleSignInResponse,
          auto_select: true,
          itp_support: true,
        });

        resolve();
      };

      if (existingScript) {
        onLoad();
        return;
      }

      const script = document.createElement('script');
      script.src = scriptSrc;
      script.onload = onLoad;

      document.head.appendChild(script);
    });

    GoogleService.init = init;
    return init;
  },

  /**
   * This function will not work properly if the provided `htmlElement` belongs to a shadow DOM.
   * This is a limitation of the Google GSI library.
   */
  renderGoogleSignInButton(htmlElement: HTMLElement): void {
    window.google.accounts.id.renderButton(htmlElement, { type: 'standard' });
  },

  showLoginPrompt: throttle(() => {
    // Throttled to run at most once every second, the GSI library breaks if called twice in a row.

    // One Tap UI
    window.google.accounts.id.prompt((notification) => {
      // eslint-disable-next-line no-console
      console.log(notification);
    });
  }, 5000),

  getUser(): GoogleUser | undefined {
    const token = Cookies.get(GoogleService.googleCookie);
    if (!token) return undefined;

    const jwtPayload = JSON.parse(atob(token.split('.')[1]));
    return jwtPayload;
  },

  isSignedIn(): boolean {
    return Boolean(Cookies.get(GoogleService.googleCookie));
  },

  signOut(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const user = GoogleService.getUser();
        if (!user) {
          resolve();
          return;
        }

        window.google.accounts.id.revoke(user.email, (response) => {
          if (!response.successful) {
            reject(response.error);
            return;
          }

          Cookies.remove(GoogleService.googleCookie);
          resolve();
        });
      } catch (err) {
        reject(err);
      }
    });
  },

  handleSignInResponse(response: google.accounts.id.CredentialResponse): void {
    Cookies.set(GoogleService.googleCookie, response.credential, {
      sameSite: 'none',
      secure: true,
    });

    window.dispatchEvent(new CustomEvent(GoogleService.LOGIN_COMPLETE_EVENT));
  },
};
